import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  LogOut, ArrowLeft, Plus, Trash2, FileDown, Image as ImageIcon, Loader2, Percent, Upload,
  Save, Send, CheckCircle2, Clock, RotateCcw, Mail,
} from "lucide-react";
import { toast } from "sonner";

import { CATS, CATS_INTERES, grupoDe, catValida, parseNum, calcRow, resumenMora } from "@/lib/intdesc";
const ORDEN_GRUPO = { "Deuda comercial": 0, "Interés por deuda": 1, "Interés por préstamo": 2 };
const usd = (n) => "US$ " + Number(n || 0).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => { if (!iso) return "—"; const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; };
const fmtMiles = (v) => {
  let s = String(v ?? "").replace(/[^\d,]/g, "");
  const parts = s.split(",");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return parts.length > 1 ? parts[0] + "," + parts.slice(1).join("").slice(0, 2) : parts[0];
};
const newRow = () => ({ id: crypto.randomUUID(), concepto: "", categoria: "Químico", vto: "", valorPago: "", fechaPago: "", tasaManual: "", saldoCorr: false });

export default function InteresesDescuentos({ user, onLogout, onBack }) {
  const [rows, setRows] = useState([newRow()]);
  const [tasaGeneral, setTasaGeneral] = useState("");
  const [cliente, setCliente] = useState("");
  const [direccion, setDireccion] = useState("");
  const [telefono, setTelefono] = useState("");
  const [fechaCalculo, setFechaCalculo] = useState(() => new Date().toISOString().slice(0, 10));
  const [modo, setModo] = useState("descuento");
  const [busy, setBusy] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [saved, setSaved] = useState([]);
  const [sendOpen, setSendOpen] = useState(false);
  const [recipients, setRecipients] = useState("");
  const [waNumbers, setWaNumbers] = useState("");
  const [sendNota, setSendNota] = useState("");
  const fileRef = useRef();

  const upd = (id, k, v) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [k]: v } : r)));
  const del = (id) => setRows((rs) => rs.filter((r) => r.id !== id));
  const add = () => setRows((rs) => [...rs, newRow()]);

  const computed = useMemo(() => rows.map((r) => ({ r, c: calcRow(r, tasaGeneral, modo, fechaCalculo) })), [rows, tasaGeneral, modo, fechaCalculo]);
  const tot = useMemo(() => computed.reduce((a, { r, c }) => ({
    valor: a.valor + parseNum(r.valorPago), interes: a.interes + c.interes,
    descuento: a.descuento + c.descuento, iva: a.iva + c.iva,
  }), { valor: 0, interes: 0, descuento: 0, iva: 0 }), [computed]);
  const resMora = useMemo(() => resumenMora(computed), [computed]);
  const neto = modo === "interes" ? resMora.total : tot.valor - tot.descuento + tot.interes;

  const loadSaved = useCallback(async () => {
    try {
      const res = await api.get("/intereses-descuentos/calculos");
      setSaved(res.data?.calculos || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const onPDF = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/intereses-descuentos/parse-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const filas = res.data?.filas || [];
      if (!filas.length) { toast.error("No se detectaron facturas con saldo en el PDF"); return; }
      if (res.data.cliente) setCliente(res.data.cliente);
      if (res.data.direccion) setDireccion(res.data.direccion);
      if (res.data.telefono) setTelefono(res.data.telefono);
      setRows(filas.map((x) => ({
        id: crypto.randomUUID(), concepto: x.concepto || "—", categoria: catValida(x.categoria),
        vto: x.vto || "", valorPago: fmtMiles(String(x.valor).replace(".", ",")), fechaPago: "", tasaManual: "",
      })));
      toast.success(`${filas.length} categoría(s) cargadas y clasificadas con IA (Gemini)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo leer el PDF");
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const buildData = (formato) => {
    const esInt = modo === "interes";
    const validas = computed.filter(({ r }) => parseNum(r.valorPago) > 0);
    const filas = (esInt
      ? [...validas].sort((a, b) => ORDEN_GRUPO[grupoDe(a.r.categoria)] - ORDEN_GRUPO[grupoDe(b.r.categoria)] || String(a.r.vto).localeCompare(String(b.r.vto)))
      : validas
    ).map(({ r, c }) => ({
      categoria: r.categoria,
      concepto: r.concepto && r.concepto !== "—" ? r.concepto : "",
      monto: parseNum(r.valorPago),
      tasa: c.tasa || parseFloat(r.tasaManual) || CATS[r.categoria] || 0,
      dias: esInt ? c.dias : Math.abs(c.dias),
      tipo: c.tipo === "—" ? (esInt ? "Interés" : "Descuento") : c.tipo,
      ajuste: c.tipo === "Interés" ? c.interes : c.descuento,
      iva: c.iva,
      ...(esInt ? {
        grupo: grupoDe(r.categoria),
        vto: fmtDate(r.vto), valor: parseNum(r.valorPago), abierto: c.abierto,
        fecha: fmtDate(c.fecha || fechaCalculo), interes: Math.round(c.interes * 100) / 100,
      } : {}),
    }));
    const first = computed.find(({ c }) => c.dias);
    return {
      formato,
      titulo: esInt ? "CÁLCULO DE INTERÉS POR MORA" : "CÁLCULO DE DESCUENTO POR PRONTO PAGO",
      subtitulo: esInt ? "Detalle de intereses por vencimiento" : "Condiciones especiales de cancelación anticipada",
      cliente: {
        nombre: cliente || "—", direccion, telefono,
        fecha_calculo: fmtDate(fechaCalculo),
        vencimiento: !esInt && first ? fmtDate(first.r.vto) : "",
        dias: !esInt && first ? Math.abs(first.c.dias) : 0,
      },
      filas,
      tot: esInt
        ? { deuda: tot.valor, descuento: 0, interes: resMora.neto, iva: resMora.iva, neto, saldo: resMora.saldo }
        : { deuda: tot.valor, descuento: tot.descuento, interes: tot.interes, iva: tot.iva, neto },
    };
  };

  const exportar = async (formato) => {
    if (!computed.some(({ c }) => c.total !== 0)) { toast.error(modo === "interes" ? "Completá al menos una línea con vencimiento, valor y la tasa de mora." : "Completá al menos una línea con vencimiento, valor y fecha de pago."); return; }
    setBusy(true);
    try {
      const res = await api.post("/intereses-descuentos/export", buildData(formato));
      if (res.data?.url) { window.open(res.data.url, "_blank"); toast.success(formato === "hd" ? "Imagen HD generada" : "PDF generado"); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo generar el reporte");
    } finally { setBusy(false); }
  };

  const buildSavePayload = () => {
    const d = buildData("pdf");
    return {
      cliente: d.cliente, modo, filas: d.filas, tot: d.tot,
      rows_raw: rows.map(({ id, ...r }) => r), tasa_general: String(tasaGeneral),
    };
  };

  const guardar = async () => {
    if (!computed.some(({ c }) => c.total !== 0)) { toast.error("Completá al menos una línea válida antes de guardar."); return null; }
    setBusy(true);
    try {
      const payload = buildSavePayload();
      const res = currentId
        ? await api.put(`/intereses-descuentos/calculos/${currentId}`, payload)
        : await api.post("/intereses-descuentos/calculos", payload);
      setCurrentId(res.data.id);
      await loadSaved();
      toast.success("Cálculo guardado");
      return res.data.id;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar el cálculo");
      return null;
    } finally { setBusy(false); }
  };

  const abrirEnvio = async () => {
    let id = currentId;
    if (!id) id = await guardar();
    if (id) { setRecipients(""); setWaNumbers(""); setSendNota(""); setSendOpen(true); }
  };

  const enviar = async () => {
    const dests = recipients.split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    const wa = waNumbers.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!dests.length && !wa.length) { toast.error("Ingresá al menos un correo @tecnomyl.com o un número de WhatsApp"); return; }
    const bad = dests.filter((e) => !e.endsWith("@tecnomyl.com"));
    if (bad.length) { toast.error(`Solo correos @tecnomyl.com: ${bad.join(", ")}`); return; }
    setBusy(true);
    try {
      let id = currentId || (await guardar());
      if (!id) return;
      const res = await api.post(`/intereses-descuentos/calculos/${id}/send`, { destinatarios: dests, whatsapp: wa, nota: sendNota });
      const parts = [];
      if (res.data.enviados_email != null) parts.push(`${res.data.enviados_email} correo(s)`);
      if (res.data.enviados_whatsapp) parts.push(`${res.data.enviados_whatsapp} WhatsApp`);
      toast.success(`Solicitud enviada: ${parts.join(" · ") || "OK"}`);
      if (res.data.fallos_whatsapp?.length) toast.warning(`WhatsApp no entregado a: ${res.data.fallos_whatsapp.join(", ")}`);
      if (res.data.fallos_email?.length) toast.warning(`Correo no enviado a: ${res.data.fallos_email.join(", ")}`);
      setSendOpen(false);
      await loadSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo enviar la solicitud");
    } finally { setBusy(false); }
  };

  const reabrir = (calc) => {
    const cli = calc.cliente || {};
    setCliente(cli.nombre === "—" ? "" : (cli.nombre || ""));
    setDireccion(cli.direccion || "");
    setTelefono(cli.telefono || "");
    setModo(calc.modo || "descuento");
    setTasaGeneral(calc.tasa_general ?? "");
    setRows((calc.rows_raw || []).map((r) => ({ id: crypto.randomUUID(), ...r, categoria: catValida(r.categoria) })));
    setCurrentId(calc.id);
    toast.success("Cálculo cargado");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const eliminar = async (calc) => {
    if (!window.confirm("¿Eliminar este cálculo guardado?")) return;
    try {
      await api.delete(`/intereses-descuentos/calculos/${calc.id}`);
      if (currentId === calc.id) setCurrentId(null);
      await loadSaved();
      toast.success("Cálculo eliminado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  const nuevo = () => {
    setRows([newRow()]); setCliente(""); setDireccion(""); setTelefono("");
    setCurrentId(null); setModo("descuento");
  };

  const inp = "h-8 w-full rounded border border-slate-200 px-2 text-xs";
  const esInt = modo === "interes";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="intdesc-module">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <Button data-testid="intdesc-back" onClick={onBack} variant="ghost" size="icon" className="text-slate-500">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <Logo className="h-8" />
            <div className="flex items-center gap-2">
              <Percent className="h-5 w-5 text-emerald-700" />
              <h1 className="text-base md:text-lg font-semibold text-[#0f5233]">Intereses y Descuentos</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 hidden sm:block">{user?.name}</span>
            <Button data-testid="intdesc-logout" onClick={onLogout} variant="ghost" size="icon" className="text-slate-500 hover:text-red-600">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 py-5 space-y-4">
        {/* Toggle Descuento / Interés */}
        <div className="flex items-center gap-2" data-testid="intdesc-modo">
          <span className="text-xs text-slate-500 mr-1">Tipo de cálculo:</span>
          <button
            data-testid="intdesc-modo-descuento"
            onClick={() => setModo("descuento")}
            className={`h-9 px-4 rounded-full text-sm font-medium transition-colors ${modo === "descuento" ? "bg-[#0f5233] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            Descuento (pronto pago)
          </button>
          <button
            data-testid="intdesc-modo-interes"
            onClick={() => setModo("interes")}
            className={`h-9 px-4 rounded-full text-sm font-medium transition-colors ${modo === "interes" ? "bg-red-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            Interés (mora)
          </button>
        </div>

        <div className={`rounded-lg border px-4 py-2.5 text-sm ${esInt ? "border-red-300 bg-red-50 text-red-800" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid="intdesc-note">
          {esInt ? (
            <>⚠️ Modo <strong>Interés por mora</strong>: cargá la tasa de mora del cliente. Si una fila tiene fecha de pago, se calcula hasta esa fecha. Marcá <strong>Saldo corregido</strong> cuando todavía no se pagó: va en su propia columna y se calcula hasta la fecha indicada (o la de cálculo si la dejás vacía). Elegí <strong>Interés por deuda</strong> o <strong>Interés por préstamo</strong> en la categoría para separarlos en el PDF. Interés = valor × tasa × (días/365) + IVA 10%. Los pagos adelantados restan como crédito (IVA incluido, tasa de la categoría).</>
          ) : (
            <>⚠️ Modo <strong>Descuento por pronto pago</strong> (base 365 días), válido para la fecha de pago indicada. Tasas con IVA incluido (Abono/Fertilizante 5% · Químico/Semilla 6%).</>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Cliente / Encabezado</label>
            <input value={cliente} onChange={(e) => setCliente(e.target.value)} placeholder="Nombre del cliente"
              className="h-9 w-56 rounded border border-slate-200 px-3 text-sm" data-testid="intdesc-cliente" />
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Dirección</label>
            <input value={direccion} onChange={(e) => setDireccion(e.target.value)} placeholder="Dirección del cliente"
              className="h-9 w-48 rounded border border-slate-200 px-3 text-sm" data-testid="intdesc-direccion" />
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Teléfono</label>
            <input value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="Teléfono"
              className="h-9 w-36 rounded border border-slate-200 px-3 text-sm" data-testid="intdesc-telefono" />
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Fecha de cálculo</label>
            <input type="date" value={fechaCalculo} onChange={(e) => setFechaCalculo(e.target.value)}
              className="h-9 w-40 rounded border border-slate-200 px-3 text-sm" data-testid="intdesc-fecha-calculo" />
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">{esInt ? "Tasa de mora (% anual, manual)" : "Tasa general (% anual)"}</label>
            <input value={tasaGeneral} onChange={(e) => setTasaGeneral(e.target.value)} type="number" step="0.5"
              className="h-9 w-32 rounded border border-slate-200 px-3 text-sm" data-testid="intdesc-tasa-general" />
          </div>
          <input ref={fileRef} type="file" accept="application/pdf" onChange={onPDF} className="hidden" data-testid="intdesc-pdf-input" />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="intdesc-upload-pdf" className="gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 text-[#0f5233]" />} Cargar PDF
          </Button>
          <Button size="sm" onClick={add} data-testid="intdesc-add-row" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4" /> Agregar fila
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={guardar} data-testid="intdesc-save" className="gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 text-[#0f5233]" />} {currentId ? "Actualizar" : "Guardar"}
          </Button>
          <Button size="sm" disabled={busy} onClick={abrirEnvio} data-testid="intdesc-open-send" className="gap-1.5 bg-[#0f5233] hover:bg-[#0c3f27]">
            <Send className="h-4 w-4" /> Enviar autorización
          </Button>
          {currentId && (
            <Button size="sm" variant="ghost" onClick={nuevo} data-testid="intdesc-new" className="gap-1.5 text-slate-500">
              <RotateCcw className="h-4 w-4" /> Nuevo
            </Button>
          )}
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" disabled={busy} onClick={() => exportar("hd")} data-testid="intdesc-export-hd" className="gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4 text-emerald-700" />} Imagen HD
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => exportar("pdf")} data-testid="intdesc-export-pdf" className="gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4 text-red-600" />} PDF
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-xs" data-testid="intdesc-table">
            <thead>
              <tr className="bg-[#0f5233] text-white">
                {["Concepto", "Categoría", "Vto. Original", esInt ? "Valor (pagado o saldo)" : "Valor Pago", esInt ? "Fecha pago / corte" : "Fecha Pago", "Días", "Tipo", "Tasa %", "Cálculo", "IVA 10%", "Total", ""].map((h) => (
                  <th key={h} className="px-2 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {computed.map(({ r, c }) => (
                <tr key={r.id} className="border-t border-slate-100 align-top" data-testid="intdesc-row">
                  <td className="px-2 py-1.5"><input value={r.concepto} onChange={(e) => upd(r.id, "concepto", e.target.value)} className={inp} placeholder="Producto / factura" /></td>
                  <td className="px-2 py-1.5">
                    <Select value={r.categoria} onValueChange={(v) => upd(r.id, "categoria", v)}>
                      <SelectTrigger className="h-8 w-40 text-xs" data-testid="intdesc-cat"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.keys(CATS).map((k) => <SelectItem key={k} value={k}>{k} ({CATS[k]}%)</SelectItem>)}
                        {esInt && CATS_INTERES.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-2 py-1.5"><input type="date" value={r.vto} onChange={(e) => upd(r.id, "vto", e.target.value)} className={inp} data-testid="intdesc-vto" /></td>
                  <td className="px-2 py-1.5"><input inputMode="decimal" value={r.valorPago} onChange={(e) => upd(r.id, "valorPago", fmtMiles(e.target.value))} className={inp + " text-right"} placeholder="0,00" data-testid="intdesc-valor" /></td>
                  <td className="px-2 py-1.5">
                    <input type="date" value={r.fechaPago} onChange={(e) => upd(r.id, "fechaPago", e.target.value)} className={inp} data-testid="intdesc-fpago" />
                    {esInt && (
                      <label className="flex items-center gap-1 mt-1 text-[10px] text-slate-600 cursor-pointer" data-testid="intdesc-saldo-corr">
                        <input type="checkbox" checked={!!r.saldoCorr} onChange={(e) => upd(r.id, "saldoCorr", e.target.checked)} />
                        Saldo corregido
                      </label>
                    )}
                    {esInt && c.abierto && r.vto && parseNum(r.valorPago) > 0 && <div className="text-[10px] text-amber-600">saldo corregido al {fmtDate(c.fecha)}</div>}
                  </td>
                  <td className="px-2 py-1.5 text-center tabular-nums">{c.dias || "—"}</td>
                  <td className={`px-2 py-1.5 text-center font-medium ${c.tipo === "Interés" ? "text-red-600" : c.tipo === "Descuento" || c.tipo === "Adelanto" ? "text-emerald-700" : "text-slate-400"}`}>{c.tipo}</td>
                  <td className="px-2 py-1.5">
                    <input type="number" value={r.tasaManual} onChange={(e) => upd(r.id, "tasaManual", e.target.value)} className={inp + " text-right"} placeholder={c.tasa || tasaGeneral} data-testid="intdesc-tasa" />
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{c.base ? usd(c.base) : "—"}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{c.tipo === "Interés" ? usd(c.iva) : (c.tipo === "Descuento" || c.tipo === "Adelanto" ? "incl." : "—")}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{c.total ? (c.total < 0 ? "− " + usd(-c.total) : usd(c.total)) : "—"}</td>
                  <td className="px-2 py-1.5 text-center">
                    <button onClick={() => del(r.id)} data-testid="intdesc-del-row" className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 border-t-2 border-slate-300 font-semibold text-slate-800">
                <td className="px-2 py-2" colSpan={3}>TOTALES</td>
                <td className="px-2 py-2 text-right tabular-nums">{usd(tot.valor)}</td>
                <td colSpan={4} />
                <td className="px-2 py-2 text-right tabular-nums text-[11px]">{esInt ? `Int neto ${usd(resMora.neto)}` : `Desc ${usd(tot.descuento)}`}</td>
                <td className="px-2 py-2 text-right tabular-nums">{usd(tot.iva)}</td>
                <td className="px-2 py-2 text-right tabular-nums text-[#0f5233]">{esInt ? usd(resMora.neto) : usd(neto)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
          {esInt && (
            <div className="px-3 py-2 text-xs text-slate-600 border-t border-slate-200 flex flex-wrap gap-x-5 gap-y-1" data-testid="intdesc-resumen-mora">
              <span>Pagado: <b>{usd(resMora.pagado)}</b></span>
              <span>Saldo abierto: <b>{usd(resMora.saldo)}</b></span>
              <span>Interés mora: <b className="text-red-600">{usd(resMora.mora)}</b></span>
              <span>Crédito adelanto: <b className="text-emerald-700">{usd(resMora.credito)}</b></span>
              <span>Total a pagar al corte: <b className="text-[#0f5233]">{usd(resMora.total)}</b></span>
            </div>
          )}
        </div>

        {/* Cálculos guardados */}
        <div className="rounded-lg border border-slate-200 bg-white" data-testid="intdesc-saved">
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
            <Save className="h-4 w-4 text-slate-500" />
            <h2 className="text-sm font-semibold text-slate-700">Cálculos guardados</h2>
            <span className="text-xs text-slate-400">({saved.length})</span>
          </div>
          {saved.length === 0 ? (
            <p className="px-4 py-6 text-sm text-slate-400 text-center">Todavía no guardaste ningún cálculo.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {saved.map((calc) => {
                const autorizado = calc.estado === "autorizado";
                return (
                  <div key={calc.id} className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid="intdesc-saved-item">
                    <div className={`flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${autorizado ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="intdesc-estado">
                      {autorizado ? <CheckCircle2 className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                      {autorizado ? "Autorizado" : "Pendiente"}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-800 truncate">{calc.cliente?.nombre || "—"}</div>
                      <div className="text-[11px] text-slate-500">
                        {calc.modo === "interes" ? "Interés" : "Descuento"} · Neto {usd(calc.tot?.neto)}
                        {autorizado && calc.autorizado_por ? ` · por ${calc.autorizado_por}` : ""}
                        {!autorizado && calc.destinatarios?.length ? ` · enviado a ${calc.destinatarios.length}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => reabrir(calc)} data-testid="intdesc-reopen" className="h-8 gap-1 text-slate-600">
                        <RotateCcw className="h-4 w-4" /> Abrir
                      </Button>
                      <button onClick={() => eliminar(calc)} data-testid="intdesc-saved-del" className="h-8 w-8 grid place-items-center text-slate-400 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Diálogo de envío */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent data-testid="intdesc-send-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-[#0f5233]" /> Enviar para autorización</DialogTitle>
            <DialogDescription>
              El correo saldrá a nombre de <strong>{user?.name}</strong> (responder-a: {user?.email}) con la imagen del cálculo y un botón para autorizar. Solo correos <strong>@tecnomyl.com</strong>. También podés enviarlo por WhatsApp.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Correos (separá por coma, espacio o enter)</label>
              <textarea value={recipients} onChange={(e) => setRecipients(e.target.value)} rows={2}
                placeholder="nombre.apellido@tecnomyl.com, otro@tecnomyl.com"
                className="w-full rounded border border-slate-200 px-3 py-2 text-sm" data-testid="intdesc-recipients" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">WhatsApp (opcional, con código de país)</label>
              <textarea value={waNumbers} onChange={(e) => setWaNumbers(e.target.value)} rows={1}
                placeholder="+595981123456, +595982..."
                className="w-full rounded border border-slate-200 px-3 py-2 text-sm" data-testid="intdesc-whatsapp" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Nota adicional (opcional)</label>
              <textarea value={sendNota} onChange={(e) => setSendNota(e.target.value)} rows={2}
                placeholder="Mensaje breve extra para el autorizador"
                className="w-full rounded border border-slate-200 px-3 py-2 text-sm" data-testid="intdesc-send-nota" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)} data-testid="intdesc-send-cancel">Cancelar</Button>
            <Button onClick={enviar} disabled={busy} data-testid="intdesc-send-confirm" className="gap-1.5 bg-[#0f5233] hover:bg-[#0c3f27]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Enviar solicitud
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
